import {
  AfterViewInit,
  afterRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  AlertComponent,
  BaseComponent,
  TopnavComponent,
  SidenavComponent,
  MenuComponent,
  AvatarComponent,
  LinkComponent,
} from '@tecalisdev/infinity';
import type { IAvatar, ISidenav, ITopnavMenu } from '@tecalisdev/infinity';
import { AuthService } from '../core/services/auth.service';
import {
  MOD_ARTICULO,
  MOD_CUENTA,
  MOD_CUOTA_PENDIENTE,
  MOD_FACTURA,
  MOD_FAMILIA,
  MOD_ROL,
  MOD_SUPPORT,
  MOD_USER,
  MODS_CONTRATOS_ACCESS,
  MODS_EXPEDIENTES_ACCESS,
} from '../core/module-ids';
import { environment } from '../../environments/environment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ListRouteReattachService } from '../core/routing/list-route-reattach.service';
import { syncCachedListUiAfterReattach } from '../core/routing/list-route-reattach.util';
import { collectNavUrls, resolveNavHighlightUrl } from '../core/utils/nav-route-active.util';
import { resolveUserFotoUrl } from '../core/utils/resolve-user-foto-url';
import { ContratoCrearNavigationService } from '../core/services/contrato-crear-navigation.service';
import { ContratoTipoModalComponent } from '../features/contratos/contrato-tipo-modal/contrato-tipo-modal.component';

/** Sidenav desde 993px; topnav con menú hasta 992px (alineado con `tec-topnav` en Infinity). */
const LAYOUT_NAV_BREAKPOINT_PX = 993;

/** Un módulo o lista OR: basta con lectura en cualquiera. */
type NavModRequirement = string | readonly string[] | null;

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    AlertComponent,
    BaseComponent,
    TopnavComponent,
    SidenavComponent,
    RouterOutlet,
    MenuComponent,
    AvatarComponent,
    LinkComponent,
    TranslatePipe,
    ContratoTipoModalComponent,
  ],
  templateUrl: './main-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements OnInit, AfterViewInit {
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly listReattach = inject(ListRouteReattachService);
  private readonly router = inject(Router);
  /** Modal Demo/Plan al crear contrato desde cualquier botón de la app. */
  readonly contratoCrearNav = inject(ContratoCrearNavigationService);

  /** Banner de entorno de prueba (Figma alert-message-warning); solo fuera de producción. */
  readonly showTestModeBanner = !environment.production;
  readonly billingLegacyUrl = environment.billingLegacyUrl ?? '';
  readonly tabletSize = signal(false);

  title = 'tecalis Billing';

  readonly sidenavConfig = signal<ISidenav[]>([]);
  readonly topnavMenu = signal<ITopnavMenu[]>([
    { label: '', icon: 'icon-user', title: 'Usuario' },
  ]);
  private readonly navUrls = signal<string[]>([]);

  @ViewChild('mainTopnav') private topnavRef?: TopnavComponent;
  @ViewChild('mainSidenav') private sidenavRef?: SidenavComponent;
  @ViewChild('topnavAvatarEl', { read: ElementRef })
  private topnavAvatarEl?: ElementRef<HTMLElement>;

  readonly user = computed(
    () => this.auth.getCurrentUser() ?? { name: '', email: '', avatar: '' },
  );

  /**
   * Infinity, ante error de `<img>`, muta `avatar.showName = true` (nombre al lado + sin tooltip).
   * Si la foto falla, omitimos `img` y recalculamos el objeto para quedar en iniciales + tooltip.
   */
  private readonly topnavImgFailed = signal(false);
  private readonly onTopnavImgError = (): void => {
    this.topnavImgFailed.set(true);
  };

  /** Resetea el fallo de foto cuando cambia la ruta guardada en sesión. */
  private readonly resetTopnavImgFailedOnAvatarChange = effect(() => {
    this.user().avatar;
    this.topnavImgFailed.set(false);
  });

  /**
   * Avatar del topnav: resuelve `foto` relativa contra `apiUrl` y omite `img` si no hay URL
   * o si la imagen ya falló. `showName: false` + `size default` → nombre solo en tooltip.
   */
  readonly topnavAvatar = computed((): IAvatar => {
    const u = this.user();
    const img = this.topnavImgFailed() ? null : resolveUserFotoUrl(u.avatar);
    const label = u.name || u.email;
    return {
      id: u.email,
      name: label,
      showName: false,
      tooltip: label,
      ...(img ? { img } : {}),
    };
  });

  private canShowNavModule(modRequirement: NavModRequirement): boolean {
    if (modRequirement == null) {
      return true;
    }
    if (!this.auth.hasModulePermissionEntries()) {
      return true;
    }
    const mods = typeof modRequirement === 'string' ? [modRequirement] : modRequirement;
    return mods.some((modId) => this.auth.canRead(modId));
  }

  private buildSidenavMainItems(t: TranslateService): ISidenav[] {
    const items: Array<ISidenav & { _mod?: NavModRequirement }> = [
      {
        label: t.instant('COMMON.PANEL'),
        url: '/panel',
        icon: 'icon-home',
        _mod: null,
      },
      {
        label: t.instant('COMMON.ACCOUNTS'),
        url: '/cuentas',
        icon: 'icon-briefcase',
        _mod: MOD_CUENTA,
      },
      {
        label: t.instant('COMMON.PARTNERS'),
        url: '/partners',
        icon: 'icon-partner',
        _mod: MOD_CUENTA,
      },
      {
        label: t.instant('COMMON.FACTURADOR'),
        url: '/facturador',
        icon: 'icon-pipeline',
        _mod: MOD_CUOTA_PENDIENTE,
      },
      {
        label: t.instant('COMMON.INVOICES_ISSUED'),
        url: '/facturas',
        icon: 'icon-euro',
        _mod: MOD_FACTURA,
      },
      {
        label: t.instant('COMMON.REMESAS'),
        url: '/remesas',
        icon: 'icon-bank',
        _mod: MOD_FACTURA,
      },
      {
        label: t.instant('COMMON.CONTRACTS'),
        url: '/contratos',
        icon: 'icon-package',
        _mod: MODS_CONTRATOS_ACCESS,
      },
      {
        label: t.instant('COMMON.EXPEDIENTES'),
        url: '/expedientes',
        icon: 'icon-numbered-list',
        _mod: MODS_EXPEDIENTES_ACCESS,
      },
      {
        label: t.instant('COMMON.ACTIVITY'),
        url: '/actividad',
        icon: 'icon-clock',
        _mod: MOD_FACTURA,
      },
    ];

    return items
      .filter((row) => this.canShowNavModule(row._mod ?? null))
      .map(({ _mod: _m, ...rest }) => rest);
  }

  private buildSidenavConfigMenu(t: TranslateService): ISidenav[] {
    type ConfigEntry = { label: string; url: string; mod: string; icon: ISidenav['icon'] };
    type ConfigSection = { titleKey: string; items: ConfigEntry[] };

    const sections: ConfigSection[] = [
      {
        titleKey: 'COMMON.CONFIG_MENU_ACCESO',
        items: [
          {
            label: t.instant('COMMON.USUARIOS'),
            url: '/configuracion/usuarios',
            mod: MOD_USER,
            icon: 'icon-user',
          },
          {
            label: t.instant('COMMON.ROLES'),
            url: '/configuracion/roles',
            mod: MOD_ROL,
            icon: 'icon-squares',
          },
        ],
      },
      {
        titleKey: 'COMMON.CONFIG_MENU_AREAS',
        items: [
          {
            label: t.instant('COMMON.REGIONES'),
            url: '/configuracion/regiones',
            mod: MOD_FAMILIA,
            icon: 'icon-connection',
          },
          {
            label: t.instant('COMMON.MERCADOS'),
            url: '/configuracion/mercado',
            mod: MOD_FAMILIA,
            icon: 'icon-store',
          },
          {
            label: t.instant('COMMON.PAISES'),
            url: '/configuracion/paises',
            mod: MOD_FAMILIA,
            icon: 'icon-map-pin',
          },
        ],
      },
      {
        titleKey: 'COMMON.CONFIG_MENU_CATALOGO',
        items: [
          {
            label: t.instant('COMMON.FAMILIAS'),
            url: '/configuracion/familias',
            mod: MOD_FAMILIA,
            icon: 'icon-workflow',
          },
          {
            label: t.instant('COMMON.ARTICULOS'),
            url: '/configuracion/articulos',
            mod: MOD_ARTICULO,
            icon: 'icon-tag',
          },
          {
            label: t.instant('COMMON.INGRESOS'),
            url: '/configuracion/tipos_ingreso',
            mod: MOD_FAMILIA,
            icon: 'icon-coin',
          },
          {
            label: t.instant('COMMON.LINEAS_DE_PRODUCTO'),
            url: '/configuracion/lineas_de_producto',
            mod: MOD_FAMILIA,
            icon: 'icon-package',
          },
          {
            label: t.instant('COMMON.CATEGORIAS_CONTABLES'),
            url: '/configuracion/categorias_contables',
            mod: MOD_FAMILIA,
            icon: 'icon-folder',
          },
        ],
      },
      {
        titleKey: 'COMMON.CONFIG_MENU_ORG_COMERCIAL',
        items: [
          {
            label: t.instant('COMMON.GRUPOS_EMPRESARIALES'),
            url: '/configuracion/grupos_empresariales',
            mod: MOD_FAMILIA,
            icon: 'icon-group',
          },
          {
            label: t.instant('COMMON.INDUSTRIAS'),
            url: '/configuracion/industria',
            mod: MOD_FAMILIA,
            icon: 'icon-layout',
          },
        ],
      },
    ];

    const menu: ISidenav[] = [];
    let firstSection = true;

    for (const section of sections) {
      const visibleItems = section.items.filter((entry) => this.canShowNavModule(entry.mod));
      if (visibleItems.length === 0) {
        continue;
      }

      visibleItems.forEach((entry, index) => {
        menu.push({
          ...(index === 0 && !firstSection ? { divider: true } : {}),
          ...(index === 0 ? { title: t.instant(section.titleKey) } : {}),
          label: entry.label,
          url: entry.url,
          icon: entry.icon,
        });
      });
      firstSection = false;
    }

    return menu;
  }

  private buildFullSidenav(t: TranslateService): ISidenav[] {
    const main = this.buildSidenavMainItems(t);
    const configMenu = this.buildSidenavConfigMenu(t);
    const items: ISidenav[] = [...main];
    if (configMenu.length > 0) {
      items.push({
        label: t.instant('COMMON.CONFIGURATION'),
        icon: 'icon-settings',
        url: '/configuracion',
        menu: configMenu,
      });
    }
    if (this.canShowNavModule(MOD_SUPPORT)) {
      items.push({
        divider: true,
        label: t.instant('COMMON.SUPPORT_NO_CONTRACT'),
        url: '/support',
        icon: 'icon-users',
      });
    }
    return items;
  }

  private buildTabletTopnavMenu(t: TranslateService): ITopnavMenu[] {
    type Row = ITopnavMenu & { _mod?: NavModRequirement };
    const rows: Row[] = [
      {
        label: t.instant('COMMON.ACCOUNTS'),
        url: '/cuentas',
        icon: 'icon-briefcase',
        title: t.instant('COMMON.ACCOUNTS'),
        _mod: MOD_CUENTA,
      },
      {
        label: t.instant('COMMON.PARTNERS'),
        url: '/partners',
        icon: 'icon-partner',
        title: t.instant('COMMON.PARTNERS'),
        _mod: MOD_CUENTA,
      },
      {
        label: t.instant('COMMON.CONTRACTS'),
        url: '/contratos',
        icon: 'icon-package',
        title: t.instant('COMMON.CONTRACTS'),
        _mod: MODS_CONTRATOS_ACCESS,
      },
      {
        label: t.instant('COMMON.EXPEDIENTES'),
        url: '/expedientes',
        icon: 'icon-folder',
        title: t.instant('COMMON.EXPEDIENTES'),
        _mod: MODS_EXPEDIENTES_ACCESS,
      },
      {
        label: t.instant('COMMON.CUOTAS'),
        url: '/cuotas',
        icon: 'icon-calendar',
        title: t.instant('COMMON.CUOTAS'),
        _mod: MOD_CUOTA_PENDIENTE,
      },
      {
        label: t.instant('COMMON.BILLING'),
        url: '/facturas',
        icon: 'icon-euro',
        title: t.instant('COMMON.BILLING'),
        _mod: MOD_FACTURA,
      },
      {
        label: t.instant('COMMON.FACTURADOR'),
        url: '/facturador',
        icon: 'icon-e-invoice',
        title: t.instant('COMMON.FACTURADOR'),
        _mod: MOD_CUOTA_PENDIENTE,
      },
    ];

    const filtered = rows
      .filter((row) => this.canShowNavModule(row._mod ?? null))
      .map(({ _mod: _m, ...rest }) => rest);

    const configMenu = this.buildSidenavConfigMenu(t);
    if (configMenu.length > 0) {
      filtered.push({
        label: t.instant('COMMON.CONFIGURATION'),
        icon: 'icon-settings',
        title: t.instant('COMMON.CONFIGURATION'),
        menu: configMenu as ITopnavMenu[],
        expanded: false,
      });
    }

    if (this.canShowNavModule(MOD_SUPPORT)) {
      filtered.push({
        label: t.instant('COMMON.SUPPORT_NO_CONTRACT'),
        url: '/support',
        icon: 'icon-users',
        title: t.instant('COMMON.SUPPORT_NO_CONTRACT'),
      });
    }

    return filtered;
  }

  private rebuildNavigationMenus(): void {
    const t = this.translateService;
    this.sidenavConfig.set(this.buildFullSidenav(t));
    this.applyTopnavForCurrentMode();
    this.refreshNavUrls();
    queueMicrotask(() => this.syncNavHighlightUrl(this.router.url));
  }

  private refreshNavUrls(): void {
    const urls = new Set<string>([
      ...collectNavUrls(this.sidenavConfig()),
      ...collectNavUrls(this.buildTabletTopnavMenu(this.translateService)),
    ]);
    this.navUrls.set([...urls]);
  }

  /** Alinea la URL activa de Infinity con rutas hijas (p. ej. `/cuentas/:id/panel`). */
  private syncNavHighlightUrl(rawUrl: string): void {
    const highlightUrl = resolveNavHighlightUrl(rawUrl, this.navUrls());
    if (this.topnavRef) {
      this.topnavRef.currentUrl = highlightUrl;
    }
    if (this.sidenavRef) {
      this.sidenavRef.currentUrl = highlightUrl;
      this.refreshSidenavExpandedState();
    }
  }

  /**
   * Reaplica highlight expandido/panel tras ajustar `currentUrl` (Infinity solo lo calcula en `ngOnChanges`).
   * En overlay (≤1200 + usePanel) no fuerza `sidePanelOpen`: Infinity ya cierra al navegar;
   * reabrir aquí dejaría el panel encima del content.
   */
  private refreshSidenavExpandedState(): void {
    const sidenav = this.sidenavRef;
    if (!sidenav) {
      return;
    }

    for (const item of sidenav.confSidenav ?? []) {
      if (!item.menu?.length) {
        continue;
      }

      const hasActiveChild = item.menu.some((subItem) => subItem.url === sidenav.currentUrl);
      if (sidenav.usePanel) {
        if (hasActiveChild) {
          sidenav.activeSidePanelItem = item;
          if (!sidenav.isPanelOverlayMode) {
            sidenav.sidePanelOpen = true;
          }
        }
      } else {
        item.expanded = item.menu.some((subItem) => subItem.url === sidenav.currentUrl);
      }
    }
  }

  private applyTopnavForCurrentMode(): void {
    const t = this.translateService;
    if (this.tabletSize()) {
      this.topnavMenu.set(this.buildTabletTopnavMenu(t));
    } else {
      this.topnavMenu.set([
        { label: '', icon: 'icon-user', title: t.instant('COMMON.USER') },
      ]);
    }
  }

  ngOnInit(): void {
    this.checkScreenSize(window.innerWidth);
    this.rebuildNavigationMenus();

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuildNavigationMenus());

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        queueMicrotask(() => this.syncNavHighlightUrl(event.urlAfterRedirects));
      });
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.syncNavHighlightUrl(this.router.url));
  }

  /** Reengancha el listener de error de la foto (Infinity también marca `showName` al fallar). */
  private readonly bindTopnavAvatarImgError = afterRender(() => {
    const img = this.topnavAvatarEl?.nativeElement?.querySelector('img');
    if (!img) return;
    img.removeEventListener('error', this.onTopnavImgError);
    img.addEventListener('error', this.onTopnavImgError);
  });


  onOutletActivate(component: unknown): void {
    if (!this.listReattach.consumePendingReattach()) {
      return;
    }
    syncCachedListUiAfterReattach(component);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize(window.innerWidth);
  }

  private checkScreenSize(width: number): void {
    const isTablet = width < LAYOUT_NAV_BREAKPOINT_PX;

    if (this.tabletSize() !== isTablet) {
      this.tabletSize.set(isTablet);
      this.applyTopnavForCurrentMode();
      this.refreshNavUrls();
      queueMicrotask(() => this.syncNavHighlightUrl(this.router.url));
    }
  }

  onMenuOption(option: { id?: string | number }): void {
    if (option?.id === 'logout') {
      this.auth.logout();
    }
    if (option?.id === 'profile') {
      // TODO: navegar a perfil si existe la ruta
    }
  }
}
